import { FormField, Input, Select, Textarea } from '@/shared/components';
import {
  invoiceBlockKindOptions,
  invoiceTaxCategoryOptions,
  type InvoiceBlockKind,
  type InvoiceTaxCategory,
} from '@/shared/api';
import type { InvoiceBlockFormState } from './invoiceBlockForm';

interface Props {
  form: InvoiceBlockFormState;
  propertyCurrency: string;
  error: string | null;
  onChange: (patch: Partial<InvoiceBlockFormState>) => void;
}

/** Die Felder des Posten-Dialogs. */
export function InvoiceBlockFields({ form, propertyCurrency, error, onChange }: Props) {
  return (
    <>
      <FormField label="Name" required>
        <Input
          value={form.label}
          onChange={(event) => onChange({ label: event.target.value })}
          placeholder="z.B. Stadttaxe Berlin"
          maxLength={200}
          autoFocus
        />
      </FormField>

      {/* Auf 360 px blieben der Berechnungsart neben dem Preisfeld rund 140 px — „Pro Aufentl"
          statt „Pro Aufenthalt". Darum erst ab `sm` nebeneinander. */}
      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <FormField label="Berechnung" required>
          <Select
            value={form.kind}
            onChange={(event) => onChange({ kind: event.target.value as InvoiceBlockKind })}
          >
            {invoiceBlockKindOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label={`Preis (${propertyCurrency})`} required>
          <Input
            type="text"
            inputMode="decimal"
            value={form.unitPrice}
            onChange={(event) => onChange({ unitPrice: event.target.value })}
            placeholder="5,00"
          />
        </FormField>
      </div>

      <FormField
        label="MwSt-Behandlung"
        required
        hint={invoiceTaxCategoryOptions.find((o) => o.value === form.taxCategory)?.hint ?? ''}
      >
        <Select
          value={form.taxCategory}
          onChange={(event) => onChange({ taxCategory: event.target.value as InvoiceTaxCategory })}
        >
          {invoiceTaxCategoryOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField label="Interne Notiz" hint="Nur intern sichtbar, nicht auf der Rechnung.">
        <Textarea
          value={form.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          rows={2}
          maxLength={2000}
        />
      </FormField>

      <label className="flex min-h-11 cursor-pointer items-center gap-3 pt-1">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => onChange({ isActive: event.target.checked })}
          className="accent-sage h-4 w-4"
        />
        <span className="text-ink text-sm">Aktiv — fließt in neue Rechnungen ein</span>
      </label>

      {error && (
        <p role="alert" className="text-sealwax text-xs">
          {error}
        </p>
      )}
    </>
  );
}
